import { useEffect, useMemo, useRef, useState } from 'react'

import { PrototypeConfig } from '@sketch-hq/sketch-prototype-player'
import { usePrototypeViewContext } from './usePrototypeViewContext'
import { useLocation } from 'react-router-dom'
import { parse } from 'query-string'
import { darkTheme } from '@sketch/global-styles'

type UsePrototypeConfigProps = {
  resolveImageSrc: ((path: string) => string) | null
}

/**
 * Returns a prototype config object that will be passed down to
 * the prototype player.
 */
export function usePrototypeConfig(
  props: UsePrototypeConfigProps
): PrototypeConfig {
  const { resolveImageSrc } = props

  const showHotzones = useShowHotzones()
  const {
    onUnhandledClickInteraction,
    currentPrototype,
  } = usePrototypeViewContext()

  const [
    currentPrototypeConfig,
    setCurrentPrototypeConfig,
  ] = useState<PrototypeConfig>({
    allowRevealHotspots: showHotzones,
    prototypeBackgroundColor: darkTheme.colors.foreground.primary.A,
    // Initialise the player to start from the artboard the user is on when opening the prototype player.
    initialArtboardId: currentPrototype.currentArtboardId,
    resolveImageSrc: resolveImageSrc || ((path: string) => path),
    onUnhandledClickInteraction,
  })

  // Save new config when any option changes.
  useEffect(() => {
    setCurrentPrototypeConfig(prevState => {
      return {
        ...prevState,
        resolveImageSrc: resolveImageSrc || ((path: string) => path),
        onUnhandledClickInteraction,
        allowRevealHotspots: showHotzones,
      }
    })
  }, [onUnhandledClickInteraction, resolveImageSrc, showHotzones])

  const currentArtboardIdRef = useRef(currentPrototype.currentArtboardId)
  currentArtboardIdRef.current = currentPrototype.currentArtboardId

  // When switching prototype, update the config to reload the prototype
  // from the new current artboard as the starting point.
  useEffect(() => {
    setCurrentPrototypeConfig(prevState => {
      const currentArtboardId = currentArtboardIdRef.current
      if (currentArtboardId !== prevState.initialArtboardId) {
        return {
          ...prevState,
          initialArtboardId: currentArtboardId,
        }
      }

      return prevState
    })
    // Important: we only want to run this effect when the prototype changes.
    // hence why we access the currentArtboardId through a ref.
  }, [currentPrototype.prototypeId])

  return currentPrototypeConfig
}

function useShowHotzones() {
  const { search } = useLocation()

  return useMemo(() => getShowHotzones(search), [search])
}

const getShowHotzones = (queryString: string) => {
  let { hotspots } = parse(queryString)
  if (!hotspots) {
    return true
  }

  // We are expecting only one query param,
  // but just in case we have multiple, take the first one.
  if (Array.isArray(hotspots)) {
    hotspots = hotspots[0]
  }

  const shouldHideHotspots =
    hotspots!.toLowerCase() === 'false' || hotspots === '0'

  return !shouldHideHotspots
}
